import { Button, Checkbox, Input, Slider } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import brandMark from "@/assets/icon.png";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import {
  type DisplayAppearance,
  defaultDisplayAppearance,
  defaultDisplaySettings,
} from "../../domain/contracts/live-workspace";
import {
  useWorkspaceEnabled,
  WorkspaceRequestError,
  workspaceErrorMessage,
  workspaceRequest,
} from "../api/workspace-client";
import { LiveOverlay } from "../ui/LiveOverlay";
import { WorkspaceSelect } from "../ui/WorkspaceProductEditor";

type Settings = { revision: number; appearance: DisplayAppearance };
type Command = Settings & { key: string };
const colors = [
  ["canvasColor", "ສີພື້ນຈໍ"],
  ["cardColor", "ສີກາດ"],
  ["textColor", "ສີຂໍ້ຄວາມ"],
  ["mutedColor", "ສີລາຄາເກົ່າ / ສະຕັອກ"],
  ["codeColor", "ສີລະຫັດ CF"],
  ["priceColor", "ສີລາຄາຂາຍ"],
  ["borderColor", "ສີຂອບ / ເສັ້ນ"],
] as const;
type ColorKey = (typeof colors)[number][0];
function normalizeHexColor(value: string): string | null {
  const hex = value.trim().replace(/^#/, "");
  return /^[0-9a-f]{6}$/i.test(hex) ? `#${hex.toLowerCase()}` : null;
}
const ranges = [
  ["opacity", "ຄວາມທຶບຂອງກາດ (%)", 0, 100, 1],
  ["fontScale", "ຂະໜາດອັກສອນ (%)", 70, 125, 1],
  ["imageScale", "ຂະໜາດຮູບ (%)", 50, 140, 1],
  ["padding", "ໄລຍະພາຍໃນກາດ (%)", 1, 4, 0.5],
  ["radius", "ຄວາມມົນຂອບ (%)", 0, 4, 0.5],
  ["borderWidth", "ຄວາມໜາຂອບ (px)", 0, 8, 1],
  ["variantsPerPage", "ຕົວເລືອກຕໍ່ໜ້າ", 1, 5, 1],
  ["pageSeconds", "ປ່ຽນໜ້າທຸກ (ວິນາທີ)", 3, 30, 1],
] as const;

export function WidgetSettingsPage() {
  const capability = useWorkspaceEnabled();
  return (
    <>
      <Header />
      <Main className="space-y-4">
        <h1 className="font-semibold text-2xl">ຕັ້ງຄ່າ Widget OBS ສ່ວນກາງ</h1>
        <p className="text-muted-foreground">
          ສີ ແລະຮູບແບບດຽວກັນສຳລັບທຸກ Live / Feed ທັງງານເດີມ ແລະງານໃໝ່.
          ຈໍທີ່ເປີດຢູ່ຈະປ່ຽນຫຼັງບັນທຶກ.
        </p>
        {capability.data?.enabled ? (
          <GlobalWidgetSettings />
        ) : (
          <p>{capability.isLoading ? "ກຳລັງໂຫຼດ…" : "ຍັງບໍ່ເປີດໃຊ້"}</p>
        )}
      </Main>
    </>
  );
}

function GlobalWidgetSettings() {
  const query = useQuery({
    queryKey: ["global-widget-settings"],
    queryFn: () => workspaceRequest<Settings>("/widget-settings"),
    refetchOnWindowFocus: false,
  });
  if (!query.data)
    return (
      <p role={query.error ? "alert" : "status"}>
        {query.error ? workspaceErrorMessage(query.error) : "ກຳລັງໂຫຼດ…"}
      </p>
    );
  return <WidgetEditor settings={query.data} />;
}

function WidgetEditor({ settings }: { settings: Settings }) {
  const client = useQueryClient();
  const { has } = usePermissions();
  const [appearance, setAppearance] = useState(settings.appearance);
  const [colorDrafts, setColorDrafts] = useState<
    Partial<Record<ColorKey, string>>
  >({});
  const [portrait, setPortrait] = useState(false);
  const [solid, setSolid] = useState(false);
  const [retry, setRetry] = useState<Command | null>(null);
  const save = useMutation({
    mutationFn: (command: Command) =>
      workspaceRequest<{ revision: number }>("/widget-settings", command),
    onSuccess: async () => {
      setRetry(null);
      await client.invalidateQueries({ queryKey: ["global-widget-settings"] });
      await client.invalidateQueries({ queryKey: ["workspace"] });
    },
    onError: (error, command) => {
      if (!(error instanceof WorkspaceRequestError)) setRetry(command);
    },
  });
  const dirty =
    JSON.stringify(appearance) !== JSON.stringify(settings.appearance) ||
    Object.keys(colorDrafts).length > 0;
  const invalidColors = colors.some(
    ([key]) => !normalizeHexColor(colorDrafts[key] ?? appearance[key]),
  );
  const disabled = !has("live:manage") || save.isPending;
  return (
    <div className="grid min-w-0 gap-6 pb-24 xl:grid-cols-2 xl:pb-0">
      <div className="space-y-5">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="min-h-11"
            disabled={disabled}
            onClick={() => {
              setAppearance(defaultDisplayAppearance);
              setColorDrafts({});
            }}
          >
            ຄ່າເລີ່ມຕົ້ນ
          </Button>
          <Button
            variant="outline"
            className="min-h-11"
            disabled={disabled}
            onClick={() => {
              setColorDrafts({});
              setAppearance({
                ...appearance,
                cardColor: "#ffffff",
                textColor: "#142131",
                mutedColor: "#516073",
                codeColor: "#006944",
                priceColor: "#142131",
                borderColor: "#d6dde5",
              });
            }}
          >
            ໂທນສະຫວ່າງ
          </Button>
        </div>
        <fieldset disabled={disabled} className="grid grid-cols-2 gap-3">
          <legend className="mb-3 font-semibold">ສີ</legend>
          {colors.map(([key, label]) => {
            const value = colorDrafts[key] ?? appearance[key];
            const invalid = !normalizeHexColor(value);
            return (
              <div key={key} className="min-w-0 space-y-1 text-sm">
                <label htmlFor={`widget-${key}-hex`}>{label}</label>
                <div className="flex items-center gap-2">
                  <Input
                    id={`widget-${key}`}
                    type="color"
                    aria-label={`${label} · ເລືອກສີ`}
                    value={appearance[key]}
                    onChange={(e) => {
                      setAppearance({ ...appearance, [key]: e.target.value });
                      setColorDrafts((previous) => {
                        const next = { ...previous };
                        delete next[key];
                        return next;
                      });
                    }}
                    className="h-11 w-14 shrink-0 p-1"
                  />
                  <Input
                    id={`widget-${key}-hex`}
                    type="text"
                    value={value}
                    placeholder="#10151d"
                    spellCheck={false}
                    autoComplete="off"
                    autoCapitalize="off"
                    aria-invalid={invalid}
                    aria-describedby={
                      invalid ? `widget-${key}-error` : undefined
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      setColorDrafts((previous) => ({
                        ...previous,
                        [key]: value,
                      }));
                      const color = normalizeHexColor(value);
                      if (color)
                        setAppearance((previous) => ({
                          ...previous,
                          [key]: color,
                        }));
                    }}
                    onBlur={() => {
                      if (normalizeHexColor(value))
                        setColorDrafts((previous) => {
                          const next = { ...previous };
                          delete next[key];
                          return next;
                        });
                    }}
                    className="min-h-11 min-w-0 flex-1 font-mono"
                  />
                </div>
                {invalid && (
                  <output
                    id={`widget-${key}-error`}
                    className="block text-destructive text-xs"
                  >
                    ກອກລະຫັດ HEX 6 ຕົວ ເຊັ່ນ #10151d
                  </output>
                )}
              </div>
            );
          })}
        </fieldset>
        <fieldset disabled={disabled} className="grid grid-cols-2 gap-3">
          <legend className="mb-3 font-semibold">ຂະໜາດ ແລະໄລຍະ</legend>
          {ranges.map(([key, label, min, max, step]) => (
            <div key={key} className="space-y-1 text-sm">
              <label id={`widget-${key}-label`} htmlFor={`widget-${key}-thumb`}>
                {label}: <strong>{appearance[key]}</strong>
              </label>
              <Slider
                id={`widget-${key}`}
                ref={(node) => {
                  // shared Slider ส่ง aria props ไปที่ root จึงผูกชื่อกับ thumb ที่รับ focus ด้วย
                  const thumb = node?.querySelector(
                    '[data-slot="slider-thumb"]',
                  );
                  thumb?.setAttribute("id", `widget-${key}-thumb`);
                  thumb?.setAttribute("aria-labelledby", `widget-${key}-label`);
                }}
                aria-labelledby={`widget-${key}-label`}
                min={min}
                max={max}
                step={step}
                value={[appearance[key]]}
                disabled={disabled}
                onValueChange={([value]) =>
                  setAppearance({
                    ...appearance,
                    [key]: value ?? appearance[key],
                  })
                }
                className="min-h-11"
              />
            </div>
          ))}
        </fieldset>
        <fieldset disabled={disabled} className="grid grid-cols-2 gap-3">
          <WorkspaceSelect
            label="ແບບອັກສອນ"
            value={appearance.fontFamily}
            items={[
              ["app", "ຟອນຂອງລະບົບ"],
              ["sans", "Sans serif"],
              ["serif", "Serif"],
            ]}
            onChange={(v) =>
              setAppearance({
                ...appearance,
                fontFamily: v as DisplayAppearance["fontFamily"],
              })
            }
          />
          <WorkspaceSelect
            label="ການວາງຮູບ"
            value={appearance.imageFit}
            items={[
              ["contain", "ເຫັນຮູບຄົບ"],
              ["cover", "ເຕັມກອບ"],
            ]}
            onChange={(v) =>
              setAppearance({
                ...appearance,
                imageFit: v as DisplayAppearance["imageFit"],
              })
            }
          />
          <label
            htmlFor="widget-shadow"
            className="flex min-h-11 items-center gap-2"
          >
            <Checkbox
              id="widget-shadow"
              checked={appearance.shadow}
              onCheckedChange={(v) =>
                setAppearance({ ...appearance, shadow: v === true })
              }
            />
            ເງົາກາດ
          </label>
        </fieldset>
        <p className="text-muted-foreground text-sm">
          ຖ້າຕົວເລືອກບໍ່ພໍພື້ນທີ່ ລະບົບຈະແບ່ງໜ້າໃຫ້ຄົບ.
        </p>
        <output aria-live="polite">
          {save.isPending
            ? "ກຳລັງບັນທຶກ…"
            : dirty
              ? "ມີການປ່ຽນແປງທີ່ຍັງບໍ່ບັນທຶກ"
              : "ບັນທຶກເທິງ server ແລ້ວ"}
        </output>
        {save.error && (
          <p role="alert" className="text-destructive">
            {workspaceErrorMessage(save.error)}
          </p>
        )}
        <div className="fixed inset-x-0 bottom-0 z-20 flex flex-wrap gap-2 border-t bg-background p-3 xl:static xl:border-0 xl:p-0">
          <Button
            className="min-h-11"
            disabled={disabled || !dirty || invalidColors || Boolean(retry)}
            onClick={() =>
              save.mutate({
                key: crypto.randomUUID(),
                revision: settings.revision,
                appearance,
              })
            }
          >
            ບັນທຶກໃຊ້ກັບທຸກ Live / Feed
          </Button>
          {retry && (
            <Button
              className="min-h-11"
              disabled={disabled}
              onClick={() => save.mutate(retry)}
            >
              ລອງບັນທຶກຄຳສັ່ງເດີມ
            </Button>
          )}
          {save.error instanceof WorkspaceRequestError &&
            save.error.code === "REVISION_CONFLICT" && (
              <Button
                className="min-h-11"
                variant="outline"
                onClick={async () => {
                  const latest =
                    await workspaceRequest<Settings>("/widget-settings");
                  client.setQueryData(["global-widget-settings"], latest);
                  setAppearance(latest.appearance);
                  setColorDrafts({});
                  save.reset();
                }}
              >
                ໂຫຼດຄ່າລ່າສຸດແທນທີ່ຮ່າງນີ້
              </Button>
            )}
        </div>
      </div>
      <aside className="order-first min-w-0 space-y-3 self-start xl:sticky xl:top-4 xl:order-last">
        <h2 className="font-semibold">ຕົວຢ່າງກ່ອນບັນທຶກ · ຂໍ້ມູນສົມມຸດ</h2>
        <p className="text-muted-foreground text-sm">
          Widget ສູງຕາມເນື້ອຫາ. ຈັດຕຳແໜ່ງ, ຂະໜາດ ແລະຕັດຂອບໄດ້ໃນ OBS.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="min-h-11"
            onClick={() => setPortrait(!portrait)}
          >
            {portrait ? "Widget ແນວຕັ້ງ" : "Widget ແນວນອນ"}
          </Button>
          <Button
            variant="outline"
            className="min-h-11"
            onClick={() => setSolid(!solid)}
          >
            {solid ? "ພື້ນທຶບ" : "ພື້ນໂປ່ງໃສ"}
          </Button>
        </div>
        <div className="overflow-hidden rounded-xl border bg-[length:20px_20px] bg-[repeating-conic-gradient(#80808020_0%_25%,transparent_0%_50%)]">
          <LiveOverlay
            display={{
              settings: {
                ...defaultDisplaySettings,
                appearance,
                orientation: portrait ? "portrait" : "landscape",
                background: solid ? "solid" : "transparent",
              },
              product: {
                id: "sample",
                title: "ເສື້ອ EZRA · ຕົວຢ່າງ",
                imageUrl: brandMark,
                variants: Array.from({ length: 8 }, (_, i) => ({
                  id: `sample-${i}`,
                  label: ["S", "M", "L", "XL"][i % 4]!,
                  cfCode: `H${i + 1}`,
                  price: 100000,
                  salePrice: 75000,
                  remaining: 12,
                })),
              },
              orders: [{ reference: "CF-DEMO", cfCode: "H1", quantity: 2 }],
              next: [],
            }}
          />
        </div>
      </aside>
    </div>
  );
}
